import React, { FC, ReactNode, useState } from 'react';
import { isTouchDevice, Tooltip } from '@investoro/core';
import clsx from 'clsx';
import styled from 'styled-components';

import Icon, { IconEnum } from 'components/common/Icon';
import { Variant } from 'components/functions/offerListFunctions/saleOfferListFunctions/saleOfferListFunctions';

const Info = styled.div`
    div {
        --font-weight: 400;
    }

    display: flex;
    color: var(--color-primary);

    & > span {
        margin-right: 15px;
    }

    div > b {
        font-weight: 500;
        color: var(--color-primary);
    }

    div > b:not(:first-child) {
        margin: 0 5px;
    }
`;

const ValueBox = styled.div<{ variant?: Variant; isHovered: boolean; minWidth?: number; }>`
    display: flex;
    gap: 6px;
    padding: 2px 6px;
    border-radius: 6px;
    cursor: pointer;
    font-style:${({ variant }) => variant && variant !== Variant.DEFAULT ? 'italic' : 'normal'};
    min-width: ${({ minWidth, variant }) => variant === Variant.DIFFERENT && minWidth ? `${minWidth}px` : 'auto'};

    div {
        display: flex;
        gap: 6px;
    }
    
    img {
        width: 20px;
        height: 20px;
    }

    .blurred {
        filter: blur(3px);
        transition: filter 0.1s ease;
    }
        
    ${({ variant }) => variant === Variant.DIFFERENT &&
    `
        background-color: var(--background-color-calculator);

        &:hover {
            background-color: var(--background-color-ai);
            color: #ffffff;
        }
    `}

    ${({ variant }) => variant === Variant.PREDICTED &&
    `
        background-color: var(--background-color-ai);
        color: #ffffff;
    `}
`;

export interface InfoElementProperties {
    icon: IconEnum;
    isVisible?: boolean;
    children: ReactNode;
    tooltipText?: string | ReactNode;
    variant?: Variant;
    minWidth?: number;
    elementKey?: string;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onClick?: () => void;
    showTooltip?: boolean;
    prefix?: string;
    suffix?: string;
    value?: string | number;
    isBlurred?: boolean;
}

const InfoElement: FC<InfoElementProperties> = ({
    isVisible,
    icon,
    children,
    tooltipText,
    onMouseEnter,
    onMouseLeave,
    onClick,
    showTooltip,
    variant,
    minWidth,
    isBlurred
}) => {
    const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);

    const handleClick = (e: React.MouseEvent | React.TouchEvent) => {
        if (isTouchDevice()) {
            e.stopPropagation();
        }
        onClick?.();
    };

    if (!isVisible) return null;

    return (
        <Info>
            {icon && <Icon icon={icon} />}
            <ValueBox
                ref={setReferenceElement}
                variant={variant}
                isHovered={!!showTooltip}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onClick={handleClick}
                minWidth={minWidth}
            >
                <div className={clsx(isBlurred && 'blurred')}>{children}</div>
                {variant && variant !== Variant.DEFAULT && <Icon icon={IconEnum.AI_STARS} />}
            </ValueBox>
            {tooltipText && showTooltip && <Tooltip text={tooltipText} referenceElement={referenceElement} showTooltip={showTooltip} />}
        </Info>
    );
};

export default InfoElement;
